import { Search, Plus, Eye, Edit, Send } from "lucide-react";
import { Link } from "react-router";
import { useMemo, useState } from "react";
import { useListClientInvoicesQuery } from "../../redux/apis/billingApi";

function formatInvoiceAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numeric);
}

function formatInvoiceDate(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/** {@code YYYY-MM} → e.g. "June 2026". */
function formatServiceMonthLabel(yyyyMm) {
  const raw = String(yyyyMm ?? "").trim();
  const match = /^(\d{4})-(\d{2})$/.exec(raw);
  if (!match) {
    return raw || "—";
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return raw;
  }
  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function normalizeStatus(status) {
  return String(status ?? "").trim();
}

function isDraftStatus(status) {
  return normalizeStatus(status).toLowerCase() === "draft";
}

function isPendingApprovalStatus(status) {
  return normalizeStatus(status).toLowerCase() === "pending approval";
}

function invoiceDetailPath(invoice) {
  const status = normalizeStatus(invoice.status);
  if (isDraftStatus(status) || isPendingApprovalStatus(status)) {
    return `/invoices/${invoice.invoiceId}/review`;
  }
  return `/invoices/${invoice.invoiceId}/view`;
}

function getStatusColor(status) {
  switch (normalizeStatus(status)) {
    case "Paid":
      return "bg-blue-600 text-white";
    case "Sent":
      return "bg-blue-500 text-white";
    case "Approved":
      return "bg-blue-400 text-white";
    case "Pending Approval":
      return "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200";
    case "Draft":
      return "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100";
    case "Overdue":
      return "bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-gray-100";
    default:
      return "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100";
  }
}

function parseInvoiceJackets(jobNo) {
  const raw = String(jobNo ?? "").trim();
  if (!raw) {
    return [];
  }
  const seen = new Set();
  const jackets = [];
  for (const part of raw.split(",")) {
    const jacket = part.trim();
    if (!jacket) {
      continue;
    }
    const key = jacket.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    jackets.push(jacket);
  }
  return jackets;
}

function InvoiceJacketsHover({ jackets }) {
  const items = jackets ?? [];
  return (
    <span className="relative inline-flex group">
      <button
        type="button"
        className="app-btn-plain text-sm font-medium text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        aria-label="View jackets for this invoice"
      >
        View Jackets
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-50 mt-2 min-w-[10rem] max-w-xs rounded-lg border border-gray-200 bg-white p-3 text-left shadow-lg opacity-0 invisible transition-[opacity,visibility] duration-150 group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible dark:border-gray-600 dark:bg-gray-900 dark:shadow-black/40"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Jackets
        </p>
        <ul className="max-h-48 space-y-1.5 overflow-y-auto text-sm text-gray-900 dark:text-gray-100">
          {items.length === 0 ? (
            <li className="italic text-gray-500 dark:text-gray-400">No jackets assigned</li>
          ) : (
            items.map((jacket) => (
              <li key={jacket} className="break-words">
                {jacket}
              </li>
            ))
          )}
        </ul>
      </span>
    </span>
  );
}

function displayInvoiceProgramName(invoice) {
  return String(invoice.invoiceProgramName ?? "").trim() || "—";
}

function displayClient(invoice) {
  const clientName = String(invoice.clientName ?? "").trim();
  if (clientName) {
    return clientName;
  }
  return String(invoice.custgroup ?? "").trim() || "—";
}

function Invoices() {
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("All Clients");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [monthFilter, setMonthFilter] = useState("All Months");
  const {
    data: invoices = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useListClientInvoicesQuery();

  const statusOptions = useMemo(() => {
    const seen = new Set();
    for (const invoice of invoices) {
      const status = normalizeStatus(invoice.status);
      if (status) {
        seen.add(status);
      }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [invoices]);

  const clientOptions = useMemo(() => {
    const seen = new Set();
    for (const invoice of invoices) {
      const client = displayClient(invoice);
      if (client && client !== "—") {
        seen.add(client);
      }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [invoices]);

  const monthOptions = useMemo(() => {
    const seen = new Set();
    for (const invoice of invoices) {
      const serviceMonth = String(invoice.serviceMonth ?? "").trim();
      if (serviceMonth) {
        seen.add(serviceMonth);
      }
    }
    return Array.from(seen).sort((a, b) => b.localeCompare(a));
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = invoices.filter((invoice) => {
      const status = normalizeStatus(invoice.status);
      if (statusFilter !== "All Status" && status !== statusFilter) {
        return false;
      }
      if (clientFilter !== "All Clients" && displayClient(invoice) !== clientFilter) {
        return false;
      }
      const serviceMonth = String(invoice.serviceMonth ?? "").trim();
      if (monthFilter !== "All Months" && serviceMonth !== monthFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      const haystack = [
        invoice.invoiceNumber,
        invoice.clientName,
        invoice.custgroup,
        invoice.jobNo,
        invoice.invoiceProgramName,
        invoice.serviceMonth,
        status,
      ]
        .map((part) => String(part ?? "").toLowerCase())
        .join(" ");
      return haystack.includes(query);
    });
    return filtered.sort((a, b) => {
      const monthA = String(a.serviceMonth ?? "").trim();
      const monthB = String(b.serviceMonth ?? "").trim();
      const monthCmp = monthB.localeCompare(monthA);
      if (monthCmp !== 0) {
        return monthCmp;
      }
      return (b.invoiceId ?? 0) - (a.invoiceId ?? 0);
    });
  }, [invoices, search, clientFilter, statusFilter, monthFilter]);

  const loadError =
    isError &&
    (typeof error?.data?.detail === "string"
      ? error.data.detail
      : "Unable to load invoices.");

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Invoices</h1>
        <p className="text-gray-600 dark:text-gray-400">Generate, review, and manage invoices</p>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div className="flex gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            aria-label="Filter by client"
          >
            <option>All Clients</option>
            {clientOptions.map((client) => (
              <option key={client} value={client}>
                {client}
              </option>
            ))}
          </select>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            aria-label="Filter by service month"
          >
            <option>All Months</option>
            {monthOptions.map((month) => (
              <option key={month} value={month}>
                {formatServiceMonthLabel(month)}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option>All Status</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <Link
          to="/invoices/new/review"
          className="app-btn-gloss ml-4"
        >
          <Plus className="w-5 h-5" />
          Create New Invoice
        </Link>
      </div>

      {loadError ? (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
          {loadError}
        </div>
      ) : null}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Invoice Program Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Jacket</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Service Month</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {isLoading || isFetching ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Loading invoices…
                  </td>
                </tr>
              ) : filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {invoices.length === 0 ? "No invoices found." : "No invoices match your filters."}
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => {
                  const status = normalizeStatus(invoice.status) || "Draft";
                  const draft = isDraftStatus(status);
                  const detailPath = invoiceDetailPath(invoice);
                  return (
                    <tr key={invoice.invoiceId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 text-sm font-medium">
                        <Link
                          to={detailPath}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                          title={draft ? "Review invoice" : "View invoice"}
                        >
                          {invoice.invoiceNumber || invoice.invoiceId}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        {displayClient(invoice)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        {displayInvoiceProgramName(invoice)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        <InvoiceJacketsHover jackets={parseInvoiceJackets(invoice.jobNo)} />
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatInvoiceAmount(invoice.invoiceAmount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatInvoiceDate(invoice.invoiceDate)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatServiceMonthLabel(invoice.serviceMonth)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(status)}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          {!draft ? (
                            <Link
                              to={detailPath}
                              className="app-btn-plain inline-flex p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="View Invoice"
                            >
                              <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </Link>
                          ) : null}
                          {draft ? (
                            <Link
                              to={detailPath}
                              className="app-btn-plain inline-flex p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Edit Invoice"
                            >
                              <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </Link>
                          ) : (
                            <button
                              type="button"
                              disabled
                              className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors opacity-40 cursor-not-allowed"
                              title="Edit Invoice"
                            >
                              <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </button>
                          )}
                          <button
                            type="button"
                            disabled
                            className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors opacity-40 cursor-not-allowed"
                            title="Send Invoice"
                          >
                            <Send className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export { Invoices };
